// ─── Unsubscribe.jsx ──────────────────────────────────────────────────────────
// Route: /unsubscribe
// Add to App.jsx:
//   import Unsubscribe from './components/legal/Unsubscribe';
//   <Route path="/unsubscribe" element={<Unsubscribe />} />
//
// URL format from emails: /unsubscribe?token=USER_TOKEN&email=user@email.com
// The token should be a unique value stored on the Firestore user document
// when they sign up — use their doc ID or a UUID stored as unsubscribeToken.
//
// FIRESTORE UPDATE:
// When confirmed, this writes to both waitlist_fans and waitlist_creators
// collections. You should also check this flag in your Cloud Function
// before sending any email:
//   if (userData.unsubscribed && userData.unsubscribeType === "all") return;
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  collection, query, where, getDocs, updateDoc, doc
} from "firebase/firestore";
import { db } from '../../firebase';
import { Logo } from "../Logo";

const OPTIONS = [
  {
    id: "marketing",
    label: "Marketing & announcements only",
    desc: "Stop receiving platform news, feature updates, and promotional emails. You'll still get account emails, payout notifications, and security alerts.",
  },
  {
    id: "updates",
    label: "Creator & fan updates",
    desc: "Stop receiving emails about new creators, content drops, and community updates. You'll still get account and payment emails.",
  },
  {
    id: "all",
    label: "Remove me from everything",
    desc: "Stop all marketing emails from 100ViewsOnly. Note: if you're an active creator, critical account and payout emails may still be sent as they are necessary to operate your account under our Terms of Service.",
  },
];

export default function Unsubscribe() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");

  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Email Preferences — 100ViewsOnly";
  }, []);

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    setError("");

    try {
      // Find the user in both collections by token or email
      const collections = ["waitlist_fans", "waitlist_creators"];
      let updated = false;

      for (const col of collections) {
        // Try to find by unsubscribeToken first, fall back to email
        const q = token
          ? query(collection(db, col), where("unsubscribeToken", "==", token))
          : query(collection(db, col), where("email", "==", emailParam?.toLowerCase()));

        const snap = await getDocs(q);
        if (!snap.empty) {
          await Promise.all(snap.docs.map(d =>
            updateDoc(doc(db, col, d.id), {
              unsubscribed: true,
              unsubscribeType: selected,         // "marketing" | "updates" | "all"
              unsubscribedAt: new Date(),
            })
          ));
          updated = true;
        }
      }

      if (!updated) {
        // Still show success — don't reveal whether email exists
        // This prevents email enumeration attacks
      }

      setSuccess(true);
    } catch (err) {
      console.error("Unsubscribe error:", err);
      setError("Something went wrong. Please email support@100viewsonly.ca and we'll sort it immediately.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: "#0a0a0a", minHeight: "100vh",
      fontFamily: "'Inter', system-ui, sans-serif", color: "#e2e8f0",
    }}>
      {/* Nav */}
      <div style={{
        background: "#0a0a0a", borderBottom: "1px solid #1e1e1e",
        padding: "14px 24px", display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10,
      }}>
        <span onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <Logo dark />
        </span>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: 520, margin: "0 auto",
        padding: "56px 24px 80px",
        display: "flex", flexDirection: "column", alignItems: "center",
      }}>

        {success ? (
          /* ── Success state ── */
          <div style={{ textAlign: "center", width: "100%" }}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>✅</div>
            <h1 style={{
              fontSize: 24, fontWeight: 900, color: "#fff",
              letterSpacing: "-0.02em", margin: "0 0 12px",
            }}>Done — preference saved.</h1>
            <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.7, margin: "0 0 24px" }}>
              Your email preference has been updated. Changes take effect immediately and are fully processed within 10 business days as required by Canadian law (CASL).
            </p>
            <div style={{
              background: "#111", border: "1px solid #1e1e1e",
              borderRadius: 10, padding: "14px 18px", marginBottom: 28, textAlign: "left",
            }}>
              <p style={{ fontSize: 12, color: "#475569", margin: 0, lineHeight: 1.7 }}>
                Changed your mind? Contact us at{" "}
                <a href="mailto:support@100viewsonly.ca" style={{ color: "#a78bfa" }}>
                  support@100viewsonly.ca
                </a>{" "}
                and we'll resubscribe you.
              </p>
            </div>
            <button
              onClick={() => navigate("/")}
              style={{
                background: "linear-gradient(135deg,#c026d3,#f472b6,#f97316)",
                border: "none", borderRadius: 30,
                padding: "12px 28px", color: "#fff",
                fontSize: 13, fontWeight: 900, cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Back to 100ViewsOnly
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <div style={{ width: "100%" }}>
            {/* Header */}
            <div style={{ marginBottom: 32, textAlign: "center" }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
                color: "#34d399", textTransform: "uppercase",
                display: "block", marginBottom: 10,
              }}>Email Preferences</span>
              <h1 style={{
                fontSize: 28, fontWeight: 900, color: "#fff",
                letterSpacing: "-0.02em", margin: "0 0 8px",
              }}>Manage Your Emails</h1>
              <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 4px", lineHeight: 1.6 }}>
                We respect your inbox. Choose what you'd like to stop receiving.
              </p>
              <p style={{ fontSize: 11, color: "#334155" }}>
                Processed immediately · Honoured within 10 business days (CASL)
              </p>
            </div>

            {/* Options */}
            <div style={{ marginBottom: 16 }}>
              {OPTIONS.map(opt => (
                <div
                  key={opt.id}
                  onClick={() => setSelected(opt.id)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 14,
                    padding: 16, borderRadius: 10, marginBottom: 8,
                    border: `1px solid ${selected === opt.id ? "#f97316" : "#1e1e1e"}`,
                    background: selected === opt.id ? "#1a080044" : "#0f0f0f",
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  {/* Radio */}
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                    border: `2px solid ${selected === opt.id ? "#f97316" : "#334155"}`,
                    background: selected === opt.id ? "#f97316" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s",
                  }}>
                    {selected === opt.id && (
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />
                    )}
                  </div>
                  {/* Text */}
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: "#fff", margin: "0 0 4px" }}>
                      {opt.label}
                    </p>
                    <p style={{ fontSize: 11, color: "#475569", margin: 0, lineHeight: 1.6 }}>
                      {opt.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <p style={{ fontSize: 12, color: "#ef4444", marginBottom: 12, textAlign: "center" }}>
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              onClick={handleConfirm}
              disabled={!selected || loading}
              style={{
                width: "100%", padding: "13px",
                borderRadius: 30, border: "none",
                background: selected && !loading
                  ? "linear-gradient(135deg,#c026d3,#f472b6,#f97316)"
                  : "#1e1e1e",
                color: selected && !loading ? "#fff" : "#334155",
                fontSize: 14, fontWeight: 900,
                cursor: selected && !loading ? "pointer" : "not-allowed",
                fontFamily: "inherit", transition: "all 0.15s",
                marginBottom: 16,
              }}
            >
              {loading ? "Saving your preference…" : "Confirm my preference"}
            </button>

            {/* Legal note */}
            <div style={{
              background: "#111", border: "1px solid #1e1e1e",
              borderRadius: 10, padding: "14px 18px",
            }}>
              <p style={{ fontSize: 10, color: "#334155", margin: 0, lineHeight: 1.7 }}>
                <strong style={{ color: "#475569" }}>Your rights under CASL & PIPEDA</strong><br />
                You have the right to withdraw consent to commercial electronic messages at any time. This unsubscribe mechanism will remain functional for a minimum of 60 days from any email sent. For data deletion requests email{" "}
                <a href="mailto:privacy@100viewsonly.ca" style={{ color: "#475569" }}>
                  privacy@100viewsonly.ca
                </a>. For complaints contact the{" "}
                <a href="https://www.priv.gc.ca" target="_blank" rel="noreferrer" style={{ color: "#475569" }}>
                  Office of the Privacy Commissioner of Canada
                </a>.
              </p>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
